import React from 'react';
import { pxToRem } from '@/modules/config';
import type { AppFeatures } from '@/modules/config';
import { ActionGuard } from '@/components/ActionGuard';
import { ActionGuardContainer } from '@/components/ActionGuardContainer';
import { ACTIONS, RESOURCES } from '@/contexts/CasbinContext';
import { FooterDock, FooterDockRow, SmartCaseButton } from '@/modules/footer';
import { OutboxLogic, useOutbox } from '@/modules/outbox';
import type { ShareUpdatesStart } from '@/modules/share/session_storage';
import type { Topic } from '../types';
import type { TopicMutation } from '../mutations';
import { latestSignificantChangeAt } from '../mutations';
import { effectiveIntroLimit } from '../intro_limit';

// The guards here sit on the buttons that start sharing, so a detour to the
// profile returns to the topic rather than to a share already under way.
const RETURN_LABEL = 'Back to topic';

interface ShareDockProps {
  topic: Topic;
  topicId: string;
  features: AppFeatures;
  topicMutations: TopicMutation[];
  onShareTopic: () => void;
  onShareUpdates: (start: ShareUpdatesStart) => void;
  onGuardSuccess: () => void;
}

/**
 * Fixed footer dock hosting the topic's primary call-to-action (share, or
 * remind once the intro limit is reached), plus an optional secondary
 * share-updates row. Renders as a sibling of `IonContent`
 * (a direct `IonPage` child) so its `FooterDock` landmark sits fixed at the
 * bottom; the page content compensates with bottom padding.
 *
 * While the topic has messages still to go in the outbox, the dock offers only
 * a way back to them: sharing again would queue a new send that replaces the
 * one in the outbox, and one prominent action keeps the page from competing
 * with the sending already under way.
 */
const ShareDock: React.FC<ShareDockProps> = ({
  topic,
  topicId,
  features,
  topicMutations,
  onShareTopic,
  onShareUpdates,
  onGuardSuccess,
}) => {
  const { sends, openOutbox } = useOutbox();
  const unsentSend = OutboxLogic.unsentSendFor(sends, topicId);

  if (unsentSend) {
    return (
      <FooterDock>
        <FooterDockRow landmark>
          <SmartCaseButton
            expand="block"
            color="primary"
            style={{ margin: 0 }}
            onClick={() => openOutbox(topicId)}
            data-testid="open-outbox-button"
          >
            Open Outbox · {OutboxLogic.sentCount(unsentSend)} of{' '}
            {unsentSend.items.length} sent
          </SmartCaseButton>
        </FooterDockRow>
      </FooterDock>
    );
  }

  const effectiveLimit = effectiveIntroLimit(
    topic.introCountLimit,
    features.introCountMax
  );
  const countLimitReached =
    effectiveLimit != null && (topic.introCount ?? 0) >= effectiveLimit;

  const shareHelpText = {
    singleVerification: 'Verify yourself to share this topic',
    updateProfile: 'Update your profile to share this topic',
    setupProfile: 'Set up your profile to share this topic',
  };

  const shareButton = (variant: 'share' | 'remind-count') => {
    const label =
      variant === 'share'
        ? 'Start the ball rolling & share'
        : 'Remind people about the topic';
    const hint = variant === 'remind-count' ? 'Max intros reached' : undefined;
    // The topics:share permission is now scoped: for an only-managers-can-share
    // topic the backend denies non-managers, so the inner ActionGuard hides the
    // share button and shows the notice in its place. No separate manager-proxy
    // guard is needed.
    const fallbackElem = topic.onlyManagersCanShare ? (
      <p
        style={{
          fontSize: pxToRem(12),
          color: '#666',
          textAlign: 'center',
          margin: 0,
        }}
        data-testid="only-managers-can-share-notice"
      >
        Only listing managers can share this topic
      </p>
    ) : undefined;
    return (
      <>
        {hint && (
          <p
            style={{
              fontSize: pxToRem(12),
              color: '#666',
              textAlign: 'center',
              margin: '0 0 4px',
            }}
          >
            {hint}
          </p>
        )}
        <ActionGuard
          disableMode="on-demand"
          action={ACTIONS.SHARE}
          resource={RESOURCES.TOPICS}
          resourceId={topicId}
          helpText={shareHelpText}
          returnLabel={RETURN_LABEL}
          fallbackElem={fallbackElem}
          guardedElem={
            <SmartCaseButton
              expand="block"
              onClick={onShareTopic}
              color="primary"
              style={{ margin: 0 }}
              data-testid="share-button"
            >
              {label}
            </SmartCaseButton>
          }
        />
      </>
    );
  };

  const variant: 'share' | 'remind-count' = countLimitReached
    ? 'remind-count'
    : 'share';

  const content = shareButton(variant);

  // Secondary "Share Updates" row (#427), offered on every topic type once this
  // session's edits cross a significance threshold: they made the changes, so
  // their change list is the one to share.
  const significantHeadAt = latestSignificantChangeAt(topicMutations);
  const updatesButton = significantHeadAt ? (
    <SmartCaseButton
      expand="block"
      fill="outline"
      color="primary"
      style={{ margin: 0 }}
      data-testid="share-updates-button"
      onClick={() =>
        onShareUpdates({ update: { aboutChangesSince: significantHeadAt } })
      }
    >
      Share updates (recommended)
    </SmartCaseButton>
  ) : null;

  return (
    <FooterDock>
      {updatesButton && (
        <FooterDockRow>
          <ActionGuardContainer>
            <ActionGuard
              disableMode="on-demand"
              action={ACTIONS.SHARE}
              resource={RESOURCES.TOPICS}
              resourceId={topicId}
              helpText={shareHelpText}
              returnLabel={RETURN_LABEL}
              guardedElem={updatesButton}
            />
          </ActionGuardContainer>
        </FooterDockRow>
      )}
      <FooterDockRow landmark>
        {/* No toastConfig: the dock says what sharing needs only when the
            button is tapped, not as soon as the topic opens. */}
        <ActionGuardContainer onSuccess={onGuardSuccess}>
          {content}
        </ActionGuardContainer>
      </FooterDockRow>
    </FooterDock>
  );
};

export default ShareDock;
